/* eslint-disable no-param-reassign */
import { ErrorMessage } from '@hookform/error-message';
import * as React from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import Button from '@/components/elements/Button/BaseButton';
import Input from '@/components/elements/Form/Input';
import { MAX_BET_COUNT } from '@/config/constants/bet';
import { getOddsRange } from '@/lib/race';
import { BetDataList, BetDetail, Odds } from '@/modelTypes';

import {
  Form,
  FundSettingWrapper,
  HundredYenSpan,
  SettingName,
  ValidMsg,
} from '../style';

interface DesiredRefundSettingProps {
  raceId: string;
  itemId?: string;
  odds: Odds;
  setBetDataList: React.Dispatch<React.SetStateAction<BetDataList>>;
  setOpenList: React.Dispatch<React.SetStateAction<string[]>>;
}

interface FormInput {
  fund: string;
}

const VALID_MSG1 = '希望払戻額は100円以上で入力してください。';
const VALID_MSG2 = '希望払戻額は半角数字のみで入力してください。';

const DesiredRefundSetting: React.FC<DesiredRefundSettingProps> = ({
  raceId,
  itemId,
  odds,
  setBetDataList,
  setOpenList,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput>({
    reValidateMode: 'onSubmit',
  });

  const setCount = (betDetails: BetDetail[], fund: number) => {
    betDetails.forEach(bd => {
      bd.tickets.forEach(ticket => {
        if (!ticket.isRemoved) {
          const oddsRange = getOddsRange(odds, [ticket.setNum], bd.betType);
          const ticketCount = Math.ceil(fund / oddsRange.min);
          ticket.count =
            ticketCount >= MAX_BET_COUNT ? MAX_BET_COUNT : ticketCount;
        }
      });
    });
  };

  const setFund = (data: FormInput) => {
    setBetDataList(prev => {
      const temp = { ...prev };
      if (itemId) {
        setCount([temp[raceId][itemId]], Number(data.fund));
        setOpenList(_prev => [..._prev, itemId]);
      } else {
        const ids: string[] = [];
        const bds: BetDetail[] = [];
        Object.keys(temp[raceId]).forEach(key => {
          ids.push(key);
          bds.push(temp[raceId][key]);
        });
        setCount(bds, Number(data.fund));
        setOpenList(_prev => [..._prev, ...ids]);
      }
      return temp;
    });
    toast.success('希望払戻額がセットされました');
  };

  return (
    <>
      <FundSettingWrapper>
        <SettingName>希望払戻額</SettingName>
        <Form onSubmit={handleSubmit(setFund)}>
          <Input
            type="tel"
            width="65px"
            height="40px"
            textAlign="right"
            hasError={Boolean(errors.fund)}
            {...register('fund', {
              required: {
                value: true,
                message: VALID_MSG1,
              },
              min: {
                value: 1,
                message: VALID_MSG1,
              },
              pattern: {
                value: /^\d+$/,
                message: VALID_MSG2,
              },
            })}
          />
          <HundredYenSpan>00円</HundredYenSpan>
          <Button
            bgColor="primary"
            size="xs"
            width="60px"
            height="40px"
            radius="m"
            type="submit"
            fontWeight="bold"
          >
            セット
          </Button>
        </Form>
      </FundSettingWrapper>
      <ErrorMessage
        errors={errors}
        name="fund"
        render={({ message }) => <ValidMsg>{message}</ValidMsg>}
      />
    </>
  );
};

export default DesiredRefundSetting;
